import { Link } from "react-router-dom";
import { ArrowRight, Users, Building2, Heart, Leaf, Sparkles, Activity, Stethoscope } from "lucide-react";
import SEO from "@/components/SEO";

const categories = [
  { icon: Stethoscope, label: "Modern Medicine" },
  { icon: Leaf, label: "Ayurveda" },
  { icon: Sparkles, label: "Wellness" },
  { icon: Activity, label: "Rehabilitation" },
  { icon: Heart, label: "Specialty Clinics" },
];

export default function SplashPage() {
  return (
    <div data-testid="splash-page" className="min-h-screen relative overflow-hidden bg-[#FAFAF7]">
      <SEO path="/" description="Flarial MediTours - World-class medical care in Kerala. We connect international patients with Kerala's best hospitals and handle everything in between." />

      {/* Soft background accents */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FAFAF7] via-white to-[#e0f2fe]/40" />
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#caf0f8]/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00A4A9]/10 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2303045e' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12">
        {/* Logo */}
        <div className="mb-8 animate-fade-in-up">
          <img
            src="/logo.png"
            alt="Flarial MediTours"
            className="w-28 h-28 sm:w-36 sm:h-36 object-contain drop-shadow-xl"
            data-testid="splash-logo"
          />
        </div>

        {/* Headline */}
        <div className="text-center max-w-3xl mb-12 animate-fade-in-up animation-delay-100">
          <h1 className="font-outfit text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-[#03045e] tracking-tight leading-tight mb-5">
            World-class care in Kerala.{" "}
            <span className="font-medium text-[#00A4A9]">Trusted by patients from across the world.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            We connect international patients with Kerala's best hospitals — and handle everything in between.
          </p>
        </div>

        {/* Two Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl mb-14 animate-fade-in-up animation-delay-200">
          <Link
            to="/patients"
            data-testid="tile-patients"
            className="group relative bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 text-center hover:border-[#00A4A9]/40 hover:shadow-[0_20px_60px_rgb(3,4,94,0.12)] transition-all duration-300 hover:-translate-y-1"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#00A4A9] flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform shadow-lg shadow-[#00A4A9]/20">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-outfit text-xl sm:text-2xl font-medium text-[#03045e] mb-3">
              I'm a Patient
            </h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Looking for world-class medical treatment in Kerala with complete travel support
            </p>
            <span className="inline-flex items-center text-sm font-medium text-[#00A4A9] group-hover:gap-2 transition-all">
              Explore Treatment Options <ArrowRight className="w-4 h-4 ml-1" />
            </span>
          </Link>

          <Link
            to="/partners"
            data-testid="tile-hospitals"
            className="group relative bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 text-center hover:border-[#48cae4]/60 hover:shadow-[0_20px_60px_rgb(3,4,94,0.12)] transition-all duration-300 hover:-translate-y-1"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#48cae4] flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform shadow-lg shadow-[#48cae4]/25">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-outfit text-xl sm:text-2xl font-medium text-[#03045e] mb-3">
              I'm a Hospital
            </h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Partner with us to connect with international patients and grow your practice
            </p>
            <span className="inline-flex items-center text-sm font-medium text-[#00A4A9] group-hover:gap-2 transition-all">
              Explore Partnership <ArrowRight className="w-4 h-4 ml-1" />
            </span>
          </Link>
        </div>

        {/* Healthcare Categories */}
        <div className="animate-fade-in-up animation-delay-300">
          <p className="text-xs text-slate-400 text-center mb-4 tracking-widest uppercase">Healthcare Areas</p>
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <div
                key={cat.label}
                className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-xs text-slate-600 shadow-sm"
              >
                <cat.icon className="w-3.5 h-3.5 text-[#00A4A9]" />
                {cat.label}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="absolute bottom-6 text-center">
          <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} Flarial MediTours. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
